<template>
    <section class="border-border-light dark:border-border-dark space-y-4 rounded-xl border p-4 sm:p-6">
        <div class="flex items-start justify-between gap-4">
            <div class="space-y-1">
                <h3>
                    {{ trans('maintainers.sla.coverage_schedule.title') }}
                </h3>
                <p>
                    {{ trans('maintainers.sla.coverage_schedule.description') }}
                </p>
            </div>

            <Button as="button" class="gap-2" size="sm" type="button" variant="outline" @click="addSchedule">
                <Plus class="h-4 w-4" />
                <span>{{ trans('maintainers.sla.coverage_schedule.actions.add_schedule') }}</span>
            </Button>
        </div>

        <div class="space-y-4">
            <article v-for="(schedule, index) in schedules" :key="schedule.id" class="border-border-light dark:border-border-dark rounded-lg border">
                <div class="border-border-light dark:border-border-dark border-b px-5 py-4">
                    <div class="flex items-center justify-between gap-4">
                        <div class="flex flex-1 items-center gap-3">
                            <div class="min-w-0 flex-1">
                                <inline-editable-field
                                    :line-height="24"
                                    :model-value="schedule.name"
                                    :name="`support-schedule-${index + 1}`"
                                    :placeholder="scheduleTitle(index)"
                                    class="w-full"
                                    @update:model-value="updateSchedule(schedule.id, { name: $event })"
                                />
                            </div>
                        </div>

                        <div class="flex items-center gap-2">
                            <button v-if="schedules.length > 1" class="p-1.5" type="button" @click="removeSchedule(schedule.id)">
                                <Trash2 class="h-4 w-4" />
                            </button>

                            <button class="p-1.5" type="button" @click="toggleCollapsed(schedule.id)">
                                <ChevronDown v-if="isCollapsed(schedule.id)" class="h-4 w-4" />
                                <ChevronUp v-else class="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    <div v-if="!isCollapsed(schedule.id)" class="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        <div class="grid h-full">
                            <div
                                class="mt-7 mb-8 flex items-center justify-between rounded-lg border border-blue-100 bg-blue-50/50 p-3 py-4 dark:border-blue-900/20 dark:bg-blue-900/10"
                            >
                                <div class="flex flex-col">
                                    <span class="text-sm font-semibold">
                                        {{ trans('maintainers.sla.coverage_schedule.all_day.title') }}
                                    </span>
                                    <span class="text-[10px]">
                                        {{ trans('maintainers.sla.coverage_schedule.all_day.description') }}
                                    </span>
                                </div>

                                <Switch
                                    :id="`support-247-${schedule.id}`"
                                    :model-value="schedule.isTwentyFourSeven"
                                    class="shrink-0 self-center"
                                    @update:model-value="toggleTwentyFourSeven(schedule.id)"
                                />
                            </div>

                            <div class="mb-3 space-y-2 self-end">
                                <label class="block">
                                    {{ trans('maintainers.sla.coverage_schedule.active_days') }}
                                </label>
                                <div class="grid grid-cols-7 gap-2">
                                    <button
                                        v-for="day in weekdayOptions"
                                        :key="day.value"
                                        :class="{
                                            '!rounded-lg border-primary bg-primary text-white disabled:cursor-not-allowed disabled:opacity-70':
                                                isDaySelected(schedule, day.value),
                                            'border-border-light dark:border-border-dark !rounded-lg bg-transparent text-body disabled:cursor-not-allowed disabled:opacity-50':
                                                !isDaySelected(schedule, day.value),
                                        }"
                                        :disabled="schedule.isTwentyFourSeven"
                                        class="h-9 border"
                                        type="button"
                                        @click="toggleDay(schedule.id, day.value)"
                                    >
                                        {{ day.label }}
                                    </button>
                                </div>
                                <div v-if="scheduleError(index, 'days')" class="mt-1 text-[13px] text-danger">
                                    {{ scheduleError(index, 'days') }}
                                </div>
                            </div>
                        </div>

                        <div class="space-y-4">
                            <div class="space-y-2">
                                <label class="block">
                                    {{ trans('locations.timezone.label') }}
                                </label>
                                <div class="space-y-2">
                                    <jet-select
                                        :horizontal="false"
                                        :invalid-feedback="scheduleError(index, 'timezone')"
                                        :model-value="schedule.timezone"
                                        :options="timezoneOptions"
                                        class="mb-0"
                                        name="timezone"
                                        @update:model-value="updateSchedule(schedule.id, { timezone: $event })"
                                    />

                                    <div class="mt-1.5 flex items-center gap-1.5">
                                        <span class="h-1.5 w-1.5 rounded-full bg-green-500" />
                                        <span class="text-subtext-light dark:text-subtext-dark text-[10px]">
                                            {{ getCurrentTimeLabel(schedule.timezone) }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div class="space-y-2">
                                <label class="block">
                                    {{ trans('maintainers.sla.coverage_schedule.timeframe') }}
                                </label>
                                <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                                    <div>
                                        <time-picker
                                            :disabled="schedule.isTwentyFourSeven"
                                            :horizontal="false"
                                            :label="trans('maintainers.sla.working_hours.from')"
                                            :model-value="toTimeDateTime(schedule.startTime, schedule.timezone)"
                                            :required="true"
                                            step="5"
                                            @update:model-value="updateSchedule(schedule.id, { startTime: fromTimeDateTime($event) })"
                                        />
                                        <div v-if="scheduleError(index, 'startTime')" class="mt-1 text-[13px] text-danger">
                                            {{ scheduleError(index, 'startTime') }}
                                        </div>
                                    </div>

                                    <div>
                                        <time-picker
                                            :disabled="schedule.isTwentyFourSeven"
                                            :horizontal="false"
                                            :label="trans('maintainers.sla.working_hours.to')"
                                            :model-value="toTimeDateTime(schedule.endTime, schedule.timezone)"
                                            :required="true"
                                            step="5"
                                            @update:model-value="updateSchedule(schedule.id, { endTime: fromTimeDateTime($event) })"
                                        />
                                        <div v-if="scheduleError(index, 'endTime')" class="mt-1 text-[13px] text-danger">
                                            {{ scheduleError(index, 'endTime') }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="!isCollapsed(schedule.id)" class="border-border-light dark:border-border-dark space-y-6 border-b p-5">
                    <div class="mb-2 flex items-center justify-between gap-4">
                        <h5>
                            <span class="h-3 w-1" />
                            {{ trans('maintainers.sla.coverage_schedule.designated_contacts') }}
                        </h5>
                        <span>
                            {{ trans('general.selected_items.label', { count: selectedContactCount(schedule) }) }}
                        </span>
                    </div>

                    <div class="space-y-3">
                        <div
                            v-for="(selectedContact, contactIndex) in selectedContacts(schedule)"
                            :key="selectedContact.id"
                            class="border-border-light dark:border-border-dark rounded-lg border p-3"
                        >
                            <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                <div class="flex min-w-0 flex-1 items-start gap-4">
                                    <div class="min-w-0 flex-1">
                                        <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
                                            <div class="min-w-0">
                                                <h6 class="mb-0 truncate text-sm font-medium">
                                                    {{ selectedContact.name }}
                                                </h6>
                                                <div v-if="selectedContact.role" class="mt-2 flex items-center gap-2">
                                                    <badge color="secondary">
                                                        {{ selectedContact.role }}
                                                    </badge>
                                                </div>
                                            </div>

                                            <label
                                                v-for="channel in supportedChannelOptions"
                                                :key="`${selectedContact.id}-${channel.type}`"
                                                :class="
                                                    isChannelAvailable(selectedContact, channel.type)
                                                        ? 'cursor-pointer'
                                                        : 'cursor-not-allowed opacity-50'
                                                "
                                                class="block"
                                            >
                                                <span
                                                    :class="{
                                                        'border-primary bg-primary/10 text-primary': isChannelSelected(
                                                            schedule,
                                                            selectedContact.id,
                                                            channel.type,
                                                        ),
                                                        'border-border-light dark:border-border-dark bg-white text-body dark:bg-transparent':
                                                            !isChannelSelected(schedule, selectedContact.id, channel.type),
                                                    }"
                                                    class="inline-flex flex-row items-center gap-2 rounded-lg border px-3 py-2 whitespace-nowrap transition-colors"
                                                >
                                                    <input
                                                        :checked="isChannelSelected(schedule, selectedContact.id, channel.type)"
                                                        :disabled="!isChannelAvailable(selectedContact, channel.type)"
                                                        class="h-3.5 w-3.5 rounded border-gray-300 text-primary"
                                                        type="checkbox"
                                                        @change="toggleChannel(schedule.id, selectedContact.id, channel.type, $event.target.checked)"
                                                    />
                                                    <component :is="channel.icon" class="h-4 w-4 shrink-0" />
                                                    <span class="text-xs font-medium">{{ trans(channel.labelKey) }}</span>
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <button class="self-end sm:self-center" type="button" @click="removeContact(schedule.id, selectedContact.id)">
                                    <X class="h-4 w-4" />
                                </button>
                            </div>
                            <div v-if="scheduleError(index, `contacts.${contactIndex}.channelTypes`)" class="mt-2 text-[13px] text-danger">
                                {{ scheduleError(index, `contacts.${contactIndex}.channelTypes`) }}
                            </div>
                        </div>

                        <div class="border-border-light dark:border-border-dark rounded-lg border p-3">
                            <div class="flex items-start justify-between gap-3">
                                <div class="flex items-center gap-2">
                                    <UserPlus class="h-4 w-4" />
                                    <span>{{ trans('maintainers.sla.coverage_schedule.actions.select_support_contacts') }}</span>
                                </div>

                                <Button
                                    as="button"
                                    class="h-8 w-8 shrink-0 p-0"
                                    size="icon"
                                    type="button"
                                    variant="outline"
                                    @click="emit('add-support-contact')"
                                >
                                    <Plus class="h-4 w-4" />
                                </Button>
                            </div>

                            <div v-if="availableContacts(schedule).length > 0" class="mt-3 flex flex-wrap gap-2">
                                <button
                                    v-for="contact in availableContacts(schedule)"
                                    :key="contact.id"
                                    class="border-border-light dark:border-border-dark inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors hover:border-primary"
                                    type="button"
                                    @click="addContact(schedule.id, contact.id)"
                                >
                                    <span class="truncate text-sm">
                                        {{ contact.name }}
                                    </span>
                                    <Plus class="text-subtext-light dark:text-subtext-dark h-3.5 w-3.5 shrink-0" />
                                </button>
                            </div>
                            <p v-else class="mt-3">
                                {{ trans('maintainers.sla.coverage_schedule.all_support_contacts_added') }}
                            </p>
                        </div>
                    </div>
                </div>
                <div v-if="!isCollapsed(schedule.id)" class="p-5">
                    <support-schedule-resolution-priorities
                        :duration-units="durationUnits"
                        :model-value="schedule.resolutionPriorities"
                        @update:model-value="updateSchedule(schedule.id, { resolutionPriorities: $event })"
                    />
                </div>
            </article>
        </div>
    </section>
</template>

<script setup>
import { usePage } from '@inertiajs/vue3';
import { DateTime } from 'luxon';
import { ChevronDown, ChevronUp, Mail, MessageSquare, Phone, Plus, Trash2, UserPlus, X } from '@lucide/vue';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import Badge from '@/Components/Badge.vue';
import Switch from '@/Components/Switch.vue';
import { Button } from '@/Components/ui/button';
import TimePicker from '@/Components/TimePicker.vue';
import JetSelect from '@/Jetstream/Select.vue';
import InlineEditableField from '@/Pages/WorkOrders/Modals/InlineEditableField.vue';
import { createSupportSchedule, resolveDefaultSupportScheduleTimezone } from '@/Pages/Stakeholders/Maintainers/supportSchedules';
import SupportScheduleResolutionPriorities from '@/Pages/Stakeholders/Maintainers/SupportScheduleResolutionPriorities.vue';
import { getWeekdays } from '@/Pages/Stakeholders/Maintainers/weekdays';
import { toTimezoneSelectOption } from '@/Utils/timezone';
import { trans } from '@/mixins/i18n';

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => [],
    },
    durationUnits: {
        type: Array,
        default: () => [],
    },
    supportContacts: {
        type: Array,
        default: () => [],
    },
    timezones: {
        type: Array,
        default: () => [],
    },
    weekdays: {
        type: Array,
        default: () => [],
    },
    supportScheduleDefaults: {
        type: Object,
        default: () => ({}),
    },
    supportedChannelTypes: {
        type: Array,
        default: () => [],
    },
    errors: {
        type: Object,
        default: () => ({}),
    },
});

const emit = defineEmits(['add-support-contact', 'update:modelValue']);

const collapsedScheduleIds = ref([]);
const currentTimestamp = ref(Date.now());
const page = usePage();
const locale = computed(() => page.props.locale ?? 'en');
const schedules = computed(() => props.modelValue ?? []);

const scheduleError = (index, field) => props.errors[`supportSchedules.${index}.${field}`] ?? null;
const timezoneOptions = computed(() => {
    if (props.timezones.length > 0) {
        return props.timezones.map(toTimezoneSelectOption);
    }

    const fallbackTimezone = resolveDefaultSupportScheduleTimezone();

    return [{ label: fallbackTimezone, value: fallbackTimezone }];
});

const weekdayValues = computed(() => props.weekdays.map((w) => w.value));
const weekdayOptions = computed(() => getWeekdays(locale.value, 'narrow').filter((day) => weekdayValues.value.includes(day.value)));

const channelOptionMap = {
    email: { type: 'email', labelKey: 'stakeholders.form.email.label', icon: Mail },
    sms: { type: 'sms', labelKey: 'maintainers.sla.coverage_schedule.channel.sms', icon: MessageSquare },
    voice: { type: 'voice', labelKey: 'maintainers.sla.coverage_schedule.channel.phone', icon: Phone },
};

const supportedChannelOptions = computed(() => props.supportedChannelTypes.map((type) => channelOptionMap[type]).filter(Boolean));

let currentTimeInterval = null;

const emitSchedules = (value) => {
    emit('update:modelValue', value);
};

const matchesTwentyFourSeven = (schedule) => {
    const coversFullWeek = weekdayValues.value.length > 0 && weekdayValues.value.every((day) => schedule.days.includes(day));
    return coversFullWeek && schedule.startTime?.slice(0, 5) === '00:00' && schedule.endTime?.slice(0, 5) === '23:59';
};

const updateSchedule = (scheduleId, patch) => {
    emitSchedules(
        schedules.value.map((schedule) => {
            if (schedule.id !== scheduleId) {
                return schedule;
            }

            const updated = { ...schedule, ...patch };

            // Auto-enable 24/7 when the user manually configures a full week and full day.
            // Skip when the toggle itself is being changed, so turning it off does not flip it back on.
            if (!('isTwentyFourSeven' in patch) && !updated.isTwentyFourSeven && matchesTwentyFourSeven(updated)) {
                updated.isTwentyFourSeven = true;
            }

            return updated;
        }),
    );
};

const addSchedule = () => {
    emitSchedules([...schedules.value, createSupportSchedule(resolveDefaultSupportScheduleTimezone(props.timezones), props.supportScheduleDefaults)]);
};

const removeSchedule = (scheduleId) => {
    if (schedules.value.length === 1) {
        return;
    }

    collapsedScheduleIds.value = collapsedScheduleIds.value.filter((id) => id !== scheduleId);
    emitSchedules(schedules.value.filter((schedule) => schedule.id !== scheduleId));
};

const toggleCollapsed = (scheduleId) => {
    collapsedScheduleIds.value = isCollapsed(scheduleId)
        ? collapsedScheduleIds.value.filter((id) => id !== scheduleId)
        : [...collapsedScheduleIds.value, scheduleId];
};

const isCollapsed = (scheduleId) => collapsedScheduleIds.value.includes(scheduleId);

const toggleTwentyFourSeven = (scheduleId) => {
    const schedule = schedules.value.find((item) => item.id === scheduleId);

    if (!schedule) {
        return;
    }

    updateSchedule(scheduleId, {
        isTwentyFourSeven: !schedule.isTwentyFourSeven,
        days: !schedule.isTwentyFourSeven ? [...weekdayValues.value] : schedule.days,
        startTime: !schedule.isTwentyFourSeven ? '00:00:00' : schedule.startTime,
        endTime: !schedule.isTwentyFourSeven ? '23:59:00' : schedule.endTime,
    });
};

const isDaySelected = (schedule, day) => schedule.days.includes(day);

const toggleDay = (scheduleId, day) => {
    const schedule = schedules.value.find((item) => item.id === scheduleId);

    if (!schedule || schedule.isTwentyFourSeven) {
        return;
    }

    const hasDay = schedule.days.includes(day);
    const days = hasDay ? schedule.days.filter((value) => value !== day) : [...schedule.days, day];

    updateSchedule(scheduleId, {
        days,
    });
};

const addContact = (scheduleId, contactId) => {
    const schedule = schedules.value.find((item) => item.id === scheduleId);

    if (!schedule || schedule.contacts.some((contact) => contact.contactId === contactId)) {
        return;
    }

    updateSchedule(scheduleId, {
        contacts: [...schedule.contacts, { contactId, channelTypes: [] }],
    });
};

const removeContact = (scheduleId, contactId) => {
    const schedule = schedules.value.find((item) => item.id === scheduleId);

    if (!schedule) {
        return;
    }

    updateSchedule(scheduleId, {
        contacts: schedule.contacts.filter((contact) => contact.contactId !== contactId),
    });
};

const toggleChannel = (scheduleId, contactId, channelType, selected) => {
    const schedule = schedules.value.find((item) => item.id === scheduleId);
    const contact = props.supportContacts.find((item) => item.id === contactId);

    if (!schedule || !isChannelAvailable(contact, channelType)) {
        return;
    }

    updateSchedule(scheduleId, {
        contacts: schedule.contacts.map((contact) => {
            if (contact.contactId !== contactId) {
                return contact;
            }

            const channelTypes = selected
                ? [...new Set([...contact.channelTypes, channelType])]
                : contact.channelTypes.filter((type) => type !== channelType);

            return {
                ...contact,
                channelTypes,
            };
        }),
    });
};

const selectedContacts = (schedule) =>
    schedule.contacts
        .map((selection) => {
            const contact = props.supportContacts.find((item) => item.id === selection.contactId);

            return contact
                ? {
                      ...contact,
                      selectedChannelTypes: selection.channelTypes,
                  }
                : null;
        })
        .filter(Boolean);

const availableContacts = (schedule) =>
    props.supportContacts.filter(
        (contact) =>
            Array.isArray(contact.channels) && contact.channels.length > 0 && !schedule.contacts.some((item) => item.contactId === contact.id),
    );

const selectedContactCount = (schedule) => schedule.contacts.length;

const isChannelSelected = (schedule, contactId, channelType) =>
    schedule.contacts.find((contact) => contact.contactId === contactId)?.channelTypes.includes(channelType) ?? false;

const isChannelAvailable = (contact, channelType) => contact?.channels?.some((channel) => channel.type === channelType) ?? false;

const toTimeDateTime = (value, timezone) => {
    const v = value || '00:00:00';
    const fmt = v.length === 5 ? 'HH:mm' : 'HH:mm:ss';
    return DateTime.fromFormat(v, fmt, { zone: timezone || 'UTC' });
};

const fromTimeDateTime = (value) => value?.toFormat?.('HH:mm:ss') ?? '00:00:00';

const scheduleTitle = (index) => trans('maintainers.sla.coverage_schedule.schedule_title', { number: index + 1 });

const getCurrentTimeLabel = (timezone) => {
    currentTimestamp.value;

    const now = DateTime.now()
        .setZone(timezone || 'UTC')
        .setLocale(locale.value);

    if (!now.isValid) {
        return trans('maintainers.sla.coverage_schedule.current_time_unavailable');
    }

    return trans('maintainers.sla.coverage_schedule.current_time', {
        time: now.toFormat('HH:mm'),
        timezone: now.offsetNameShort ?? timezone,
    });
};

onMounted(() => {
    currentTimeInterval = window.setInterval(() => {
        currentTimestamp.value = Date.now();
    }, 60000);
});

onBeforeUnmount(() => {
    if (currentTimeInterval) {
        window.clearInterval(currentTimeInterval);
    }
});
</script>
